import "./url.js";
import "@sveltejs/kit/internal/server";
import "./root.js";
import { b as private_env } from "./shared-server.js";
import "./utils.js";
import { b as command, q as query } from "./query.js";
import "./shared.js";
import { error } from "@sveltejs/kit";
import { init_remote_functions } from "@sveltejs/kit/internal";
import { p as parseJSON, v as validateAggregationPipeline, i as isEmptyObject } from "./jsonParser.js";
import { J as JsonEncoder } from "./JsonEncoder.js";
import { l as logger } from "./logger.js";
import { g as getMongo, s as sanitizeMongoUri } from "./mongo.js";
import { a as auditSchemaCompliance } from "./schema.js";
import { ObjectId, ReadPreference } from "mongodb";
import { z } from "zod";
const m = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  get addServer() {
    return addServer;
  },
  get auditSchema() {
    return auditSchema;
  },
  get countDocuments() {
    return countDocuments;
  },
  get countDocumentsByTimeRange() {
    return countDocumentsByTimeRange;
  },
  get createIndex() {
    return createIndex;
  },
  get createUser() {
    return createUser;
  },
  get deleteDocument() {
    return deleteDocument;
  },
  get deleteMany() {
    return deleteMany;
  },
  get detectPrimaryNode() {
    return detectPrimaryNode;
  },
  get dropCollection() {
    return dropCollection;
  },
  get dropDatabase() {
    return dropDatabase;
  },
  get dropIndex() {
    return dropIndex;
  },
  get dropUser() {
    return dropUser;
  },
  get explainQuery() {
    return explainQuery;
  },
  get fetchMappedDocument() {
    return fetchMappedDocument;
  },
  get getIndexStatsFromNodes() {
    return getIndexStatsFromNodes;
  },
  get getIndexStatsWithReadPreference() {
    return getIndexStatsWithReadPreference;
  },
  get getServerNodes() {
    return getServerNodes;
  },
  get grantRolesToUser() {
    return grantRolesToUser;
  },
  get hideIndex() {
    return hideIndex;
  },
  get insertDocument() {
    return insertDocument;
  },
  get listRoles() {
    return listRoles;
  },
  get listUsers() {
    return listUsers;
  },
  get loadDocuments() {
    return loadDocuments;
  },
  get removeServer() {
    return removeServer;
  },
  get retryConnection() {
    return retryConnection;
  },
  get revokeRolesFromUser() {
    return revokeRolesFromUser;
  },
  get unhideIndex() {
    return unhideIndex;
  },
  get updateDocument() {
    return updateDocument;
  },
  get updateMany() {
    return updateMany;
  },
  get updateUserPassword() {
    return updateUserPassword;
  }
}, Symbol.toStringTag, { value: "Module" }));
function checkReadOnly() {
  if (private_env.MONGOKU_READ_ONLY_MODE === "true") {
    error(403, "Read-only mode is enabled");
  }
}
function sanitizeMongoUrl(url) {
  return sanitizeMongoUri(url.startsWith("mongodb") ? url : `mongodb://${url}`);
}
const addServer = command(
  z.object({
    url: z.string()
  }),
  async ({ url }) => {
    logger.log("addServer called with payload:", { url: sanitizeMongoUrl(url) });
    const mongo = await getMongo();
    await mongo.addServer(url);
    return { ok: true };
  }
);
const removeServer = command(z.string(), async (serverName) => {
  logger.log("removeServer called with payload:", { serverName });
  const mongo = await getMongo();
  await mongo.removeServer(serverName);
  return { ok: true };
});
const updateDocument = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    document: z.string(),
    value: z.unknown(),
    partial: z.boolean().optional().default(false),
    upsert: z.boolean().optional().default(false)
  }),
  async ({ server, database, collection, document, value, partial, upsert }) => {
    logger.log("updateDocument called with payload:", { server, database, collection, document, partial, upsert });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    const newValue = JsonEncoder.decode(value);
    const _id = /^[0-9a-fA-F]{24}$/.test(document) ? new ObjectId(document) : document;
    if (partial) {
      await coll.updateOne(
        {
          _id
        },
        { $set: newValue },
        { upsert }
      );
    } else {
      await coll.replaceOne(
        {
          _id
        },
        JsonEncoder.decode(newValue),
        { upsert }
      );
    }
    if (collection === "mongoku.mappings") {
      client.clearMappingsCache(database, document);
    }
    return {
      ok: true,
      update: JsonEncoder.encode(newValue)
    };
  }
);
const insertDocument = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    document: z.string().nullable().optional(),
    value: z.unknown()
  }),
  async ({ server, database, collection, document, value }) => {
    logger.log("insertDocument called with payload:", { server, database, collection, document });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    const newValue = JsonEncoder.decode(value);
    if (document !== null && document !== void 0) {
      const _id = /^[0-9a-fA-F]{24}$/.test(document) ? new ObjectId(document) : document;
      newValue._id = _id;
    }
    const res = await coll.insertOne(newValue);
    if (collection === "mongoku.mappings" && typeof res.insertedId === "string") {
      client.clearMappingsCache(database, res.insertedId);
    }
    return {
      ok: true,
      insert: JsonEncoder.encode(newValue)
    };
  }
);
const deleteDocument = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    document: z.string()
  }),
  async ({ server, database, collection, document }) => {
    logger.log("deleteDocument called with payload:", { server, database, collection, document });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const _id = /^[0-9a-fA-F]{24}$/.test(document) ? new ObjectId(document) : document;
    await client.db(database).collection(collection).deleteOne({
      _id
    });
    if (collection === "mongoku.mappings") {
      client.clearMappingsCache(database, document);
    }
    return {
      ok: true
    };
  }
);
const updateMany = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    filter: z.string(),
    update: z.string()
  }),
  async ({ server, database, collection, filter, update }) => {
    logger.log("updateMany called with payload:", { server, database, collection, filter, update });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    const filterDoc = JsonEncoder.decode(parseJSON(filter));
    const updateDoc = JsonEncoder.decode(parseJSON(update, { allowArray: true }));
    const result = await coll.updateMany(filterDoc, updateDoc);
    return {
      ok: true,
      matchedCount: result.matchedCount,
      modifiedCount: result.modifiedCount
    };
  }
);
const countDocuments = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    filter: z.string()
  }),
  async ({ server, database, collection, filter }) => {
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    const filterDoc = JsonEncoder.decode(parseJSON(filter));
    try {
      const count = await coll.countDocuments(filterDoc, {
        maxTimeMS: mongo.getCountTimeout()
      });
      return {
        data: count,
        error: null
      };
    } catch (err) {
      logger.error("Error counting documents:", err);
      return {
        data: 0,
        error: `Failed to count documents: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const deleteMany = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    filter: z.string()
  }),
  async ({ server, database, collection, filter }) => {
    logger.log("deleteMany called with payload:", { server, database, collection, filter });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    const filterDoc = JsonEncoder.decode(parseJSON(filter));
    const result = await coll.deleteMany(filterDoc);
    return {
      ok: true,
      deletedCount: result.deletedCount
    };
  }
);
const hideIndex = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    index: z.string()
  }),
  async ({ server, database, collection, index }) => {
    logger.log("hideIndex called with payload:", { server, database, collection, index });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    await client.db(database).command({
      collMod: collection,
      index: {
        name: index,
        hidden: true
      }
    });
    return {
      ok: true
    };
  }
);
const unhideIndex = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    index: z.string()
  }),
  async ({ server, database, collection, index }) => {
    logger.log("unhideIndex called with payload:", { server, database, collection, index });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    await client.db(database).command({
      collMod: collection,
      index: {
        name: index,
        hidden: false
      }
    });
    return {
      ok: true
    };
  }
);
const createIndex = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    keys: z.string(),
    name: z.string().optional(),
    unique: z.boolean().optional(),
    sparse: z.boolean().optional(),
    partialFilterExpression: z.string().optional(),
    expireAfterSeconds: z.number().optional(),
    background: z.boolean().optional()
  }),
  async ({
    server,
    database,
    collection,
    keys,
    name,
    unique,
    sparse,
    partialFilterExpression,
    expireAfterSeconds,
    background
  }) => {
    logger.log("createIndex called with payload:", { server, database, collection, keys, name, unique, sparse });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    const keysDoc = JsonEncoder.decode(parseJSON(keys));
    const options = {};
    if (name) {
      options.name = name;
    }
    if (unique) {
      options.unique = unique;
    }
    if (sparse) {
      options.sparse = sparse;
    }
    if (partialFilterExpression) {
      options.partialFilterExpression = JsonEncoder.decode(parseJSON(partialFilterExpression));
    }
    if (expireAfterSeconds !== void 0) {
      options.expireAfterSeconds = expireAfterSeconds;
    }
    if (background) {
      options.background = background;
    }
    await coll.createIndex(keysDoc, options);
    return {
      ok: true
    };
  }
);
const dropIndex = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    index: z.string()
  }),
  async ({ server, database, collection, index }) => {
    logger.log("dropIndex called with payload:", { server, database, collection, index });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    await client.db(database).command({
      dropIndexes: collection,
      index
    });
    return {
      ok: true
    };
  }
);
const dropCollection = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string()
  }),
  async ({ server, database, collection }) => {
    logger.log("dropCollection called with payload:", { server, database, collection });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const db = client.db(database);
    await db.dropCollection(collection);
    return {
      ok: true
    };
  }
);
const dropDatabase = command(
  z.object({
    server: z.string(),
    database: z.string()
  }),
  async ({ server, database }) => {
    logger.log("dropDatabase called with payload:", { server, database });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    await client.db(database).dropDatabase();
    return {
      ok: true
    };
  }
);
const retryConnection = command(z.string(), async (serverId) => {
  logger.log("retryConnection called with payload:", { serverId });
  const mongo = await getMongo();
  await mongo.reconnectClient(serverId);
  return { ok: true };
});
const loadDocuments = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    query: z.string().default("{}"),
    sort: z.string().default("{}"),
    project: z.string().default("{}"),
    skip: z.number().int().default(0),
    limit: z.number().int().default(20),
    mode: z.enum(["query", "distinct", "aggregation"]).default("query"),
    field: z.string().optional()
  }),
  async ({ server, database, collection, query: queryStr, sort, project, skip, limit, mode, field }) => {
    let queryDoc;
    try {
      queryDoc = parseJSON(queryStr, { allowArray: true });
    } catch (err) {
      error(400, `Invalid query: ${err}`);
    }
    try {
      parseJSON(sort);
    } catch (err) {
      error(400, `Invalid sort: ${err}`);
    }
    try {
      parseJSON(project);
    } catch (err) {
      error(400, `Invalid project: ${err}`);
    }
    const sortDoc = parseJSON(sort);
    const projectDoc = parseJSON(project);
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    if (mode === "distinct") {
      if (!field) {
        error(400, "Invalid distinct query: field name is required");
      }
      try {
        const results = await coll.distinct(field, JsonEncoder.decode(queryDoc), {
          maxTimeMS: mongo.getQueryTimeout()
        });
        return {
          data: results.map((value) => JsonEncoder.encode({ value })),
          error: null,
          isAggregation: false,
          isDistinct: true
        };
      } catch (err) {
        logger.error("Error executing distinct:", err);
        error(500, `Failed to execute distinct: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    if (mode === "aggregation" && Array.isArray(queryDoc)) {
      try {
        validateAggregationPipeline(queryDoc);
      } catch (err) {
        error(400, `Invalid aggregation pipeline: ${err instanceof Error ? err.message : String(err)}`);
      }
      const pipeline = JsonEncoder.decode(queryDoc);
      try {
        const results = await coll.aggregate(
          [
            ...pipeline,
            ...isEmptyObject(projectDoc) ? [] : [{ $project: projectDoc }],
            ...isEmptyObject(sortDoc) ? [] : [{ $sort: sortDoc }],
            { $limit: limit },
            { $skip: skip }
          ],
          {
            maxTimeMS: mongo.getQueryTimeout()
          }
        ).map((obj) => JsonEncoder.encode(obj)).toArray();
        return {
          data: results,
          error: null,
          isAggregation: true,
          isDistinct: false
        };
      } catch (err) {
        logger.error("Error executing aggregation:", err);
        error(500, `Failed to execute aggregation: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    try {
      const results = await coll.find(JsonEncoder.decode(queryDoc), { maxTimeMS: mongo.getQueryTimeout() }).project(projectDoc).sort(JsonEncoder.decode(sortDoc)).limit(limit).skip(skip).map((obj) => JsonEncoder.encode(obj)).toArray();
      return {
        data: results,
        error: null,
        isAggregation: false,
        isDistinct: false
      };
    } catch (err) {
      logger.error("Error fetching query results:", err);
      error(500, `Failed to fetch query results: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
);
const fetchMappedDocument = query(
  z.object({
    server: z.string(),
    database: z.string(),
    mappings: z.array(
      z.union([
        z.object({
          type: z.literal("document"),
          collection: z.string(),
          on: z.string()
        }),
        z.object({
          type: z.literal("url"),
          template: z.string()
        }),
        // Legacy format (backwards compatibility)
        z.object({
          collection: z.string(),
          on: z.string()
        })
      ])
    ),
    value: z.unknown()
  }),
  async ({ server, database, mappings, value }) => {
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const decodedValue = JsonEncoder.decode(value);
    const documentMappings = mappings.filter((m2) => {
      if ("type" in m2) {
        return m2.type === "document";
      }
      return "collection" in m2 && "on" in m2;
    });
    for (const mapping of documentMappings) {
      try {
        if ("type" in mapping && mapping.type !== "document") {
          continue;
        }
        const collection = "collection" in mapping ? mapping.collection : "";
        const on = "on" in mapping ? mapping.on : "_id";
        const coll = client.db(database).collection(collection);
        const query2 = { [on]: decodedValue };
        const document = await coll.findOne(query2, { maxTimeMS: mongo.getQueryTimeout() });
        if (document) {
          return {
            data: JsonEncoder.encode(document),
            collection,
            error: null
          };
        }
      } catch (err) {
        const collection = "collection" in mapping ? mapping.collection : "unknown";
        const on = "on" in mapping ? mapping.on : "_id";
        logger.error(`Error fetching mapped document from ${collection}.${on}:`, err);
        continue;
      }
    }
    return {
      data: null,
      collection: null,
      error: "Document not found in any mapped collection"
    };
  }
);
const getIndexStatsWithReadPreference = query(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    readPreferenceMode: z.string().optional(),
    readPreferenceTags: z.string().optional()
  }),
  async ({ server, database, collection, readPreferenceMode, readPreferenceTags }) => {
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    try {
      let tags;
      if (readPreferenceTags) {
        try {
          tags = parseJSON(readPreferenceTags);
          if (typeof tags !== "object" || tags === null) {
            error(400, "Invalid read preference tags format");
          }
          if (!Array.isArray(tags)) {
            tags = [tags];
          }
        } catch (err) {
          error(400, `Invalid read preference tags JSON: ${err}`);
        }
      }
      let readPreference;
      if (readPreferenceMode || tags) {
        const mode = readPreferenceMode || "nearest";
        readPreference = tags ? new ReadPreference(mode, tags) : new ReadPreference(mode);
      }
      const coll = client.db(database).collection(collection);
      const aggregateOptions = readPreference ? { readPreference } : {};
      const statsResult = await coll.aggregate([{ $indexStats: {} }], aggregateOptions).toArray();
      const indexStats = Object.fromEntries(
        statsResult.map((stat) => [
          stat.name,
          {
            ops: stat.accesses?.ops || 0,
            since: stat.accesses?.since || /* @__PURE__ */ new Date(),
            host: stat.host || "unknown"
          }
        ])
      );
      return {
        data: JsonEncoder.encode(indexStats),
        error: null
      };
    } catch (err) {
      logger.error("Error fetching index stats with read preference:", err);
      return {
        data: {},
        error: `Failed to fetch index stats: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const getServerNodes = query(
  z.object({
    server: z.string()
  }),
  async ({ server }) => {
    const mongo = await getMongo();
    try {
      const nodes = await mongo.getServerNodes(server);
      return {
        data: nodes,
        error: null
      };
    } catch (err) {
      logger.error("Error getting server nodes:", err);
      return {
        data: [],
        error: `Failed to get server nodes: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const detectPrimaryNode = query(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string()
  }),
  async ({ server, database, collection }) => {
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    try {
      const coll = client.db(database).collection(collection);
      const statsResult = await coll.aggregate([{ $indexStats: {} }], { readPreference: new ReadPreference("primary") }).toArray();
      const primaryHost = statsResult.length > 0 ? statsResult[0].host : null;
      return {
        data: primaryHost,
        error: null
      };
    } catch (err) {
      logger.error("Error detecting primary node:", err);
      return {
        data: null,
        error: `Failed to detect primary: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const getIndexStatsFromNodes = query(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    nodes: z.array(z.string())
  }),
  async ({ server, database, collection, nodes }) => {
    const mongo = await getMongo();
    try {
      const results = await Promise.allSettled(
        nodes.map(async (node) => {
          const stats = await mongo.getIndexStatsFromNode(server, node, database, collection);
          return { node, stats };
        })
      );
      const mergedStats = {};
      const errors = [];
      let i = 0;
      for (const result of results) {
        if (result.status === "fulfilled") {
          const { stats } = result.value;
          for (const [indexName, indexStats] of Object.entries(stats)) {
            const key = `${indexName}::${indexStats.host}`;
            mergedStats[key] = indexStats;
          }
        } else {
          logger.error(`Error fetching index stats from node ${nodes[i]}:`, result.reason);
          errors.push(result.reason?.message || String(result.reason));
        }
        i++;
      }
      return {
        data: JsonEncoder.encode(mergedStats),
        error: errors.length > 0 ? errors.join("; ") : null
      };
    } catch (err) {
      logger.error("Error fetching index stats from nodes:", err);
      return {
        data: {},
        error: `Failed to fetch index stats: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const explainQuery = query(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    query: z.string().default("{}"),
    sort: z.string().default("{}"),
    project: z.string().default("{}"),
    skip: z.number().int().default(0),
    limit: z.number().int().default(20),
    mode: z.enum(["query", "aggregation"]).default("query"),
    verbosity: z.enum(["queryPlanner", "executionStats", "allPlansExecution"]).default("executionStats")
  }),
  async ({ server, database, collection, query: queryStr, sort, project, skip, limit, mode, verbosity }) => {
    const queryDoc = (() => {
      try {
        return parseJSON(queryStr, { allowArray: true });
      } catch (err) {
        error(400, `Invalid query: ${err}`);
      }
    })();
    try {
      parseJSON(sort);
    } catch (err) {
      error(400, `Invalid sort: ${err}`);
    }
    try {
      parseJSON(project);
    } catch (err) {
      error(400, `Invalid project: ${err}`);
    }
    const sortDoc = parseJSON(sort);
    const projectDoc = parseJSON(project);
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    try {
      let explainResult;
      if (mode === "aggregation" && Array.isArray(queryDoc)) {
        try {
          validateAggregationPipeline(queryDoc);
        } catch (err) {
          error(400, `Invalid aggregation pipeline: ${err instanceof Error ? err.message : String(err)}`);
        }
        const pipeline = JsonEncoder.decode(queryDoc);
        const fullPipeline = [
          ...pipeline,
          ...isEmptyObject(projectDoc) ? [] : [{ $project: projectDoc }],
          ...isEmptyObject(sortDoc) ? [] : [{ $sort: sortDoc }],
          { $skip: skip },
          { $limit: limit }
        ];
        explainResult = await coll.aggregate(fullPipeline).explain(verbosity);
      } else {
        explainResult = await coll.find(JsonEncoder.decode(queryDoc)).project(projectDoc).sort(JsonEncoder.decode(sortDoc)).skip(skip).limit(limit).explain(verbosity);
      }
      const plainResult = JSON.parse(JSON.stringify(explainResult));
      return {
        data: plainResult,
        error: null
      };
    } catch (err) {
      logger.error("Error explaining query:", err);
      return {
        data: null,
        error: `Failed to explain query: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const countDocumentsByTimeRange = query(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    days: z.number(),
    query: z.string().optional()
  }),
  async ({
    server,
    database,
    collection,
    days,
    query: queryStr
  }) => {
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const coll = client.db(database).collection(collection);
    let baseQuery = {};
    if (queryStr) {
      try {
        const parsed = parseJSON(queryStr);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          baseQuery = JsonEncoder.encode(parsed);
        }
      } catch {
      }
    }
    let useCreatedAt = false;
    try {
      const sample = await coll.findOne({}, { projection: { _id: 1, createdAt: 1 }, maxTimeMS: 5e3 });
      if (sample) {
        const idIsObjectId = sample._id instanceof ObjectId;
        if (!idIsObjectId) {
          if (sample.createdAt instanceof Date && await client.hasIndexOnField(database, collection, "createdAt")) {
            useCreatedAt = true;
          } else {
            return { count: null, error: "Cannot determine document age (no ObjectId or createdAt field)" };
          }
        }
      }
    } catch {
    }
    const dateThreshold = /* @__PURE__ */ new Date();
    dateThreshold.setDate(dateThreshold.getDate() - days);
    let filter;
    if (useCreatedAt) {
      filter = { ...baseQuery, createdAt: { $gte: dateThreshold } };
    } else {
      const objectIdThreshold = ObjectId.createFromTime(Math.floor(dateThreshold.getTime() / 1e3));
      filter = { ...baseQuery, _id: { $gte: objectIdThreshold } };
    }
    try {
      const count = await coll.countDocuments(filter, { maxTimeMS: mongo.getCountTimeout() });
      return { count, error: null };
    } catch (err) {
      logger.error(`Error counting documents for ${days} days:`, err);
      const errorMsg = err instanceof Error ? err.message : String(err);
      return { count: null, error: errorMsg };
    }
  }
);
const auditSchema = command(
  z.object({
    server: z.string(),
    database: z.string(),
    collection: z.string(),
    readPreferenceMode: z.string().optional(),
    readPreferenceTags: z.string().optional()
  }),
  async ({ server, database, collection, readPreferenceMode, readPreferenceTags }) => {
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    try {
      let readPreference;
      if (readPreferenceMode) {
        let tags;
        if (readPreferenceTags) {
          try {
            const parsed = parseJSON(readPreferenceTags, { allowArray: true });
            if (Array.isArray(parsed)) {
              tags = parsed;
            } else if (parsed && typeof parsed === "object") {
              tags = [parsed];
            }
          } catch {
          }
        }
        readPreference = tags ? new ReadPreference(
          readPreferenceMode,
          tags
        ) : new ReadPreference(
          readPreferenceMode
        );
      }
      const result = await auditSchemaCompliance(client, database, collection, {
        readPreference,
        maxTimeMS: mongo.getQueryTimeout()
      });
      return {
        data: JsonEncoder.encode(result),
        error: null
      };
    } catch (err) {
      logger.error("Error auditing schema compliance:", err);
      return {
        data: null,
        error: `Failed to audit schema: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const roleRefSchema = z.union([
  z.string(),
  z.object({
    role: z.string(),
    db: z.string()
  })
]);
const listUsers = query(z.object({ server: z.string() }), async ({ server }) => {
  logger.log("listUsers called with payload:", { server });
  const mongo = await getMongo();
  const client = mongo.getClient(server);
  const admin = client.db("admin");
  try {
    const result = await admin.command({ usersInfo: { forAllDBs: true } });
    const users = result.users ?? [];
    const usersWithPrivs = await Promise.all(
      users.map(async (u) => {
        try {
          const info = await admin.command({
            usersInfo: { user: u.user, db: u.db },
            showPrivileges: true
          });
          const detail = (info.users ?? [])[0] ?? {};
          return { ...u, ...detail };
        } catch (err) {
          logger.error(`Error getting privileges for user ${u.user}:`, err);
          return u;
        }
      })
    );
    return {
      // JSON round-trip strips MongoDB special types for safe transport
      data: JSON.parse(JSON.stringify(JsonEncoder.encode(usersWithPrivs))),
      error: null
    };
  } catch (err) {
    logger.error("Error listing users:", err);
    return {
      data: [],
      error: `Failed to list users: ${err instanceof Error ? err.message : String(err)}`
    };
  }
});
const listRoles = query(z.object({ server: z.string() }), async ({ server }) => {
  logger.log("listRoles called with payload:", { server });
  const mongo = await getMongo();
  const client = mongo.getClient(server);
  const admin = client.db("admin");
  try {
    const result = await admin.command({ rolesInfo: 1, showBuiltinRoles: true, showPrivileges: true });
    return {
      data: JSON.parse(JSON.stringify(JsonEncoder.encode(result.roles ?? []))),
      error: null
    };
  } catch (err) {
    logger.error("Error listing roles:", err);
    return {
      data: [],
      error: `Failed to list roles: ${err instanceof Error ? err.message : String(err)}`
    };
  }
});
const createUser = command(
  z.object({
    server: z.string(),
    username: z.string(),
    password: z.string(),
    db: z.string().default("admin"),
    roles: z.array(roleRefSchema).default([])
  }),
  async ({ server, username, password, db, roles }) => {
    logger.log("createUser called with payload:", { server, username, db, roles });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const targetDb = client.db(db);
    try {
      const decodedRoles = JsonEncoder.decode(roles);
      await targetDb.command({
        createUser: username,
        pwd: password,
        roles: decodedRoles
      });
      return { ok: true, error: null };
    } catch (err) {
      logger.error("Error creating user:", err);
      return {
        ok: false,
        error: `Failed to create user: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const dropUser = command(
  z.object({
    server: z.string(),
    username: z.string(),
    db: z.string().default("admin")
  }),
  async ({ server, username, db }) => {
    logger.log("dropUser called with payload:", { server, username, db });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const targetDb = client.db(db);
    try {
      await targetDb.command({ dropUser: username });
      return { ok: true, error: null };
    } catch (err) {
      logger.error("Error dropping user:", err);
      return {
        ok: false,
        error: `Failed to drop user: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const grantRolesToUser = command(
  z.object({
    server: z.string(),
    username: z.string(),
    db: z.string().default("admin"),
    roles: z.array(roleRefSchema)
  }),
  async ({ server, username, db, roles }) => {
    logger.log("grantRolesToUser called with payload:", { server, username, db, roles });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const targetDb = client.db(db);
    try {
      const decodedRoles = JsonEncoder.decode(roles);
      await targetDb.command({
        grantRolesToUser: username,
        roles: decodedRoles
      });
      return { ok: true, error: null };
    } catch (err) {
      logger.error("Error granting roles to user:", err);
      return {
        ok: false,
        error: `Failed to grant roles: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const revokeRolesFromUser = command(
  z.object({
    server: z.string(),
    username: z.string(),
    db: z.string().default("admin"),
    roles: z.array(roleRefSchema)
  }),
  async ({ server, username, db, roles }) => {
    logger.log("revokeRolesFromUser called with payload:", { server, username, db, roles });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const targetDb = client.db(db);
    try {
      const decodedRoles = JsonEncoder.decode(roles);
      await targetDb.command({
        revokeRolesFromUser: username,
        roles: decodedRoles
      });
      return { ok: true, error: null };
    } catch (err) {
      logger.error("Error revoking roles from user:", err);
      return {
        ok: false,
        error: `Failed to revoke roles: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
const updateUserPassword = command(
  z.object({
    server: z.string(),
    username: z.string(),
    db: z.string().default("admin"),
    password: z.string()
  }),
  async ({ server, username, db, password }) => {
    logger.log("updateUserPassword called with payload:", { server, username, db });
    checkReadOnly();
    const mongo = await getMongo();
    const client = mongo.getClient(server);
    const targetDb = client.db(db);
    try {
      await targetDb.command({
        updateUser: username,
        pwd: password
      });
      return { ok: true, error: null };
    } catch (err) {
      logger.error("Error updating user password:", err);
      return {
        ok: false,
        error: `Failed to update password: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  }
);
init_remote_functions(m, "src/api/servers.remote.ts", "xxtqbu");
for (const [name, fn] of Object.entries(m)) {
  fn.__.id = "xxtqbu/" + name;
  fn.__.name = name;
}
export {
  countDocuments as c,
  deleteDocument as d,
  explainQuery as e,
  fetchMappedDocument as f,
  loadDocuments as l,
  m,
  updateDocument as u
};
